/**
 * WebSocket Service for Real-Time Communication
 *
 * Provides real-time execution event streaming via WebSocket connection.
 * Features:
 * - Auto-reconnect with exponential backoff
 * - Execution subscription management
 * - Event history replay on subscribe
 * - Connection state management
 */

import { getAccessToken } from '../lib/supabase.ts';
import type { ExecutionEvent, ExecutionEventType } from './api.ts';

// WebSocket URL - use WSS in production, WS for local dev
const WS_BASE = import.meta.env.VITE_WS_URL ||
  (import.meta.env.PROD ? 'wss://api.aigentswork.com/ws' : 'ws://localhost:3001/ws');

// ============================================================================
// Types
// ============================================================================

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

export interface WSMessage<T = unknown> {
  type: string;
  expressionId: string;
  timestamp: number;
  payload: T;
}

export interface ExecutionEventPayload {
  executionId: string;
  eventType: ExecutionEventType;
  agentId?: string;
  agentName?: string;
  payload: unknown;
  timestamp: string;
  durationMs?: number;
  sequenceNumber: number;
}

export interface ExecutionHistoryPayload {
  executionId: string;
  events: ExecutionEventPayload[];
  totalCount: number;
}

export type ExecutionEventHandler = (event: ExecutionEvent) => void;
export type ConnectionStateHandler = (state: ConnectionState) => void;

interface ExecutionSubscription {
  executionId: string;
  handlers: Set<ExecutionEventHandler>;
  agentIdFilter?: string;
}

// ============================================================================
// WebSocket Service
// ============================================================================

class WebSocketService {
  private ws: WebSocket | null = null;
  private connectionState: ConnectionState = 'disconnected';
  private connectionStateHandlers: Set<ConnectionStateHandler> = new Set();
  private subscriptions: Map<string, ExecutionSubscription> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private baseReconnectDelay = 1000; // 1 second
  private maxReconnectDelay = 30000; // 30 seconds
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * Connect to the WebSocket server
   */
  async connect(): Promise<void> {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.setConnectionState('connecting');

    try {
      const token = await getAccessToken();
      const wsUrl = token ? `${WS_BASE}?token=${encodeURIComponent(token)}` : WS_BASE;

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[WebSocket] Connected');
        this.setConnectionState('connected');
        this.reconnectAttempts = 0;
        this.startPing();

        // Re-subscribe to any active subscriptions after reconnect
        this.resubscribeAll();
      };

      this.ws.onclose = (event) => {
        console.log('[WebSocket] Disconnected:', event.code, event.reason);
        this.stopPing();
        this.setConnectionState('disconnected');

        // Auto-reconnect if not intentionally closed
        if (event.code !== 1000) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
      };

      this.ws.onmessage = (event) => {
        this.handleMessage(event.data);
      };
    } catch (error) {
      console.error('[WebSocket] Connection error:', error);
      this.setConnectionState('disconnected');
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    this.stopPing();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.setConnectionState('disconnected');
    this.subscriptions.clear();
  }

  /**
   * Subscribe to execution events
   */
  subscribeToExecution(
    executionId: string,
    handler: ExecutionEventHandler,
    options?: { replayHistory?: boolean; agentIdFilter?: string },
  ): () => void {
    // Ensure connection
    if (this.connectionState === 'disconnected') {
      this.connect();
    }

    // Get or create subscription
    let subscription = this.subscriptions.get(executionId);
    if (!subscription) {
      subscription = {
        executionId,
        handlers: new Set(),
        agentIdFilter: options?.agentIdFilter,
      };
      this.subscriptions.set(executionId, subscription);

      // Send subscription message if connected
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.sendSubscribe(executionId, options);
      }
    }

    subscription.handlers.add(handler);

    // Return unsubscribe function
    return () => {
      this.unsubscribeFromExecution(executionId, handler);
    };
  }

  /**
   * Unsubscribe from execution events
   */
  unsubscribeFromExecution(executionId: string, handler: ExecutionEventHandler): void {
    const subscription = this.subscriptions.get(executionId);
    if (!subscription) return;

    subscription.handlers.delete(handler);

    // If no more handlers, remove subscription entirely
    if (subscription.handlers.size === 0) {
      this.subscriptions.delete(executionId);

      // Send unsubscribe message if connected
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.sendUnsubscribe(executionId);
      }
    }
  }

  /**
   * Add connection state change handler
   */
  onConnectionStateChange(handler: ConnectionStateHandler): () => void {
    this.connectionStateHandlers.add(handler);
    // Immediately notify of current state
    handler(this.connectionState);

    return () => {
      this.connectionStateHandlers.delete(handler);
    };
  }

  /**
   * Get current connection state
   */
  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  // ============================================================================
  // Private methods
  // ============================================================================

  private setConnectionState(state: ConnectionState): void {
    this.connectionState = state;
    this.connectionStateHandlers.forEach((handler) => handler(state));
  }

  private handleMessage(data: string): void {
    try {
      const message = JSON.parse(data) as WSMessage;

      switch (message.type) {
        case 'execution_event':
          this.handleExecutionEvent(message.payload as ExecutionEventPayload);
          break;
        case 'execution_history':
          this.handleExecutionHistory(message.payload as ExecutionHistoryPayload);
          break;
        case 'error':
          console.error('[WebSocket] Server error:', message.payload);
          break;
        default:
          // Other message types (data_update, expression_update, etc.)
          break;
      }
    } catch (error) {
      console.error('[WebSocket] Failed to parse message:', error);
    }
  }

  private handleExecutionEvent(payload: ExecutionEventPayload): void {
    const subscription = this.subscriptions.get(payload.executionId);
    if (!subscription) return;

    // Apply agent filter if set
    if (subscription.agentIdFilter && payload.agentId !== subscription.agentIdFilter) {
      return;
    }

    const event: ExecutionEvent = {
      id: `${payload.executionId}-${payload.sequenceNumber}`,
      executionId: payload.executionId,
      eventType: payload.eventType,
      agentId: payload.agentId,
      agentName: payload.agentName,
      payload: payload.payload,
      timestamp: payload.timestamp,
      durationMs: payload.durationMs,
      sequenceNumber: payload.sequenceNumber,
    };

    subscription.handlers.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        console.error('[WebSocket] Event handler error:', error);
      }
    });
  }

  private handleExecutionHistory(payload: ExecutionHistoryPayload): void {
    const subscription = this.subscriptions.get(payload.executionId);
    if (!subscription) return;

    // Replay each event in order
    for (const eventPayload of payload.events) {
      this.handleExecutionEvent(eventPayload);
    }
  }

  private sendSubscribe(
    executionId: string,
    options?: { replayHistory?: boolean; agentIdFilter?: string },
  ): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    this.ws.send(JSON.stringify({
      type: 'execution_subscribe',
      payload: {
        executionId,
        replayHistory: options?.replayHistory !== false,
        agentIdFilter: options?.agentIdFilter,
      },
    }));
  }

  private sendUnsubscribe(executionId: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    this.ws.send(JSON.stringify({
      type: 'execution_unsubscribe',
      payload: { executionId },
    }));
  }

  private resubscribeAll(): void {
    this.subscriptions.forEach((subscription, executionId) => {
      this.sendSubscribe(executionId, {
        replayHistory: true,
        agentIdFilter: subscription.agentIdFilter,
      });
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[WebSocket] Max reconnect attempts reached');
      return;
    }

    this.setConnectionState('reconnecting');
    this.reconnectAttempts++;

    // Exponential backoff with jitter
    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts - 1) + Math.random() * 1000,
      this.maxReconnectDelay,
    );

    console.log(`[WebSocket] Reconnecting in ${Math.round(delay / 1000)}s (attempt ${this.reconnectAttempts})`);

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private startPing(): void {
    // Send ping every 30 seconds to keep connection alive
    this.pingTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();

// Export class for testing
export { WebSocketService };
